import {
  SocialChannelStatusReason,
  SocialRunGroupItem,
  SocialRunStopReason,
  SocialRunPhase,
  SocialSyncRunStatus,
  SocialTrackerStatus,
  SocialUniverseChannel,
} from '@digital-twins/shared-types'

export type SocialStatusTone = 'positive' | 'progress' | 'warning' | 'destructive' | 'muted'

const IN_PROGRESS_STATUSES: ReadonlyArray<SocialTrackerStatus> = [
  'queued',
  'collecting',
  'indexing',
]

const RETRYABLE_STATUSES: ReadonlyArray<SocialTrackerStatus> = ['partial', 'failed']

/** A channel's status and its server-typed reason (absent on older servers). */
export interface SocialStatusWithReason {
  status: SocialTrackerStatus
  statusReason?: SocialChannelStatusReason | null
  sortingStopped?: boolean
}

/** Reasons a collect is refused for a missing key: the workspace has no social listening yet. */
export type SocialNotConfiguredReason = Extract<
  SocialChannelStatusReason,
  'judge_not_configured' | 'provider_not_configured'
>

export function isNotConfiguredReason(
  reason: SocialChannelStatusReason | null | undefined,
): reason is SocialNotConfiguredReason {
  return reason === 'judge_not_configured' || reason === 'provider_not_configured'
}

export function isTrackerInProgress(status: SocialTrackerStatus): boolean {
  return IN_PROGRESS_STATUSES.includes(status)
}

/**
 * A channel whose update is really queued or running. A paused channel never
 * is, whatever its status says; nor is one whose in-progress status was left
 * behind by an attempt whose run already finished (the last run closed at or
 * after the channel's last attempt, and no run is open).
 */
export function isChannelInProgress(
  channel: Pick<
    SocialUniverseChannel,
    'status' | 'pauseReason' | 'lastAttemptAt' | 'lastRun' | 'progress'
  >,
): boolean {
  if (!isTrackerInProgress(channel.status) || channel.pauseReason != null) {
    return false
  }
  if (channel.progress) {
    return true
  }
  const finishedAt = channel.lastRun?.finishedAt ? Date.parse(channel.lastRun.finishedAt) : Number.NaN
  const attemptAt = channel.lastAttemptAt ? Date.parse(channel.lastAttemptAt) : Number.NaN
  if (Number.isNaN(finishedAt) || Number.isNaN(attemptAt)) {
    return true
  }
  return finishedAt < attemptAt
}

/**
 * Partial and failed offer Retry; every other status offers Run now. A
 * partial channel whose posts were all collected and only wait to be sorted
 * did update: sorting retries on its own, and a new collect would not help.
 * Once sorting stopped (its final ask failed) only a new update asks again.
 */
export function isTrackerRetryable({
  status,
  statusReason,
  sortingStopped,
}: SocialStatusWithReason): boolean {
  return (
    RETRYABLE_STATUSES.includes(status) && (statusReason !== 'not_sorted' || sortingStopped === true)
  )
}

/** The i18n key of a status label: its typed reason's when it has its own words. */
export function trackerStatusLabelKey({
  status,
  statusReason,
  sortingStopped,
}: SocialStatusWithReason):
  | 'socialListening.statusReason.not_sorted'
  | 'socialListening.statusReason.not_sorted_stopped'
  | `socialListening.status.${SocialTrackerStatus}` {
  if (status === 'partial' && statusReason === 'not_sorted') {
    return sortingStopped
      ? 'socialListening.statusReason.not_sorted_stopped'
      : 'socialListening.statusReason.not_sorted'
  }
  return `socialListening.status.${status}`
}

export function trackerStatusTone(status: SocialTrackerStatus): SocialStatusTone {
  switch (status) {
    case 'ready':
      return 'positive'
    case 'queued':
    case 'collecting':
    case 'indexing':
      return 'progress'
    case 'partial':
    case 'rate_limited':
    case 'over_budget':
      return 'warning'
    case 'failed':
      return 'destructive'
    case 'paused':
      return 'muted'
  }
}

export function runStatusTone(status: SocialSyncRunStatus): SocialStatusTone {
  switch (status) {
    case 'succeeded':
      return 'positive'
    case 'queued':
    case 'running':
    case 'indexing':
      return 'progress'
    case 'partial':
    case 'rate_limited':
    case 'over_budget':
      return 'warning'
    case 'failed':
      return 'destructive'
    case 'skipped':
      return 'muted'
  }
}

type RunStatusLabelKey =
  | `socialListening.runStatus.${Exclude<SocialSyncRunStatus, 'over_budget'>}`
  | 'socialListening.status.paused'

/**
 * A run badge's copy key. A run closed at the internal spend guard
 * (`over_budget`) reads "Paused": customers never see a budget, and out of
 * credits runs carry their own reason line.
 */
export function runStatusLabelKey(status: SocialSyncRunStatus): RunStatusLabelKey {
  if (status === 'over_budget') {
    return 'socialListening.status.paused'
  }
  return `socialListening.runStatus.${status}`
}

/**
 * A channel's badge tone with its pause reason (E8): paused for credits reads
 * as a warning (the user can act on it); paused by the user or for
 * maintenance stays muted.
 */
export function channelStatusTone(
  channel: Pick<SocialUniverseChannel, 'status' | 'pauseReason'>,
): SocialStatusTone {
  if (channel.status === 'paused' && channel.pauseReason === 'out_of_credits') {
    return 'warning'
  }
  return trackerStatusTone(channel.status)
}

/**
 * When a channel's posts last changed, for the "Updated …" line: its last
 * success, or the latest closed run when that run stored accepted posts
 * (a run cut by the monthly limit keeps what it accepted). Null before either.
 */
export function socialChannelUpdatedAt(
  channel: Pick<SocialUniverseChannel, 'lastSyncedAt' | 'lastRun'>,
): string | null {
  const run = channel.lastRun
  const split = run?.sourceSplit
  const storedPosts = split
    ? split.organic + split.brand_owned + split.sponsored + split.unknown
    : 0
  const runAt = run?.finishedAt && storedPosts > 0 ? run.finishedAt : null
  return latestTimestamp(channel.lastSyncedAt, runAt)
}

/** A Topic's "Updated …" date: the latest of its channels', or the server's last success. */
export function socialUniverseUpdatedAt(universe: {
  lastSyncedAt: string | null
  channels: ReadonlyArray<Pick<SocialUniverseChannel, 'lastSyncedAt' | 'lastRun'>>
}): string | null {
  return universe.channels.reduce<string | null>(
    (latest, channel) => latestTimestamp(latest, socialChannelUpdatedAt(channel)),
    universe.lastSyncedAt,
  )
}

function latestTimestamp(a: string | null, b: string | null): string | null {
  if (a === null) return b
  if (b === null) return a
  return Date.parse(b) > Date.parse(a) ? b : a
}

/** Run phases after collection and before the posts are listed: shown as "Sorting". */
const SORTING_PHASES: ReadonlyArray<SocialRunPhase> = ['media', 'judge', 'digest']

const TROUBLE_STATUSES: ReadonlyArray<SocialTrackerStatus> = [
  'partial',
  'failed',
  'rate_limited',
  'over_budget',
]

/** True while new posts are collected but not yet listed (images, tags, Library). */
export function isRunPhaseSorting(phase: SocialRunPhase | null | undefined): boolean {
  if (!phase) {
    return false
  }
  return SORTING_PHASES.includes(phase)
}

/** True while any channel of a Topic is collecting or sorting. */
export function isUniverseInProgress(
  channels: ReadonlyArray<Pick<SocialUniverseChannel, 'status' | 'phase'>>,
): boolean {
  return channels.some(
    (channel) => isTrackerInProgress(channel.status) || isRunPhaseSorting(channel.phase),
  )
}

/** Channels that offer Retry ("Reddit could not update, earlier posts kept"). */
export function universeChannelsNeedingRetry<T extends SocialStatusWithReason>(
  channels: ReadonlyArray<T>,
): Array<T> {
  return channels.filter((channel) => isTrackerRetryable(channel))
}

/**
 * One tone for a Topic from its channels: progress while any is in progress;
 * muted when none or all are paused; destructive when every active channel
 * failed; warning when some are in trouble or stale; positive otherwise.
 */
export function universeStatusTone(
  channels: ReadonlyArray<Pick<SocialUniverseChannel, 'status' | 'stale' | 'phase'>>,
): SocialStatusTone {
  if (isUniverseInProgress(channels)) {
    return 'progress'
  }
  const active = channels.filter((channel) => channel.status !== 'paused')
  if (active.length === 0) {
    return 'muted'
  }
  if (active.every((channel) => channel.status === 'failed')) {
    return 'destructive'
  }
  if (active.some((channel) => TROUBLE_STATUSES.includes(channel.status) || channel.stale)) {
    return 'warning'
  }
  return 'positive'
}

export const STATUS_TONE_BADGE_VARIANT: Record<
  SocialStatusTone,
  'positive' | 'info' | 'warning' | 'soft-destructive' | 'muted'
> = {
  positive: 'positive',
  progress: 'info',
  warning: 'warning',
  destructive: 'soft-destructive',
  muted: 'muted',
}

/** Why a closed run brought fewer relevant posts than it aimed for (D3). */
export type SocialShortRunKind = 'exhausted' | 'off_topic'

export interface SocialShortRun {
  kind: SocialShortRunKind
  /** The widest look-back the run read; null when its groups state none. */
  readDays: number | null
}

/** The typed close of a run or a channel's last run: what a short-run line reads. */
export interface SocialShortRunInput {
  stopReason?: SocialRunStopReason | null
  groups?: Array<Pick<SocialRunGroupItem, 'target' | 'accepted' | 'readDays'> & Partial<Pick<SocialRunGroupItem, 'kind'>>>
}

/**
 * A run that closed under its target, and why: `exhausted` (every source ran
 * out: that's everything from the window) or `off_topic` (the fetch budget
 * ran out on posts the judge turned down). Null for a run that met its
 * target, one still open, or one from before rounds (no groups).
 */
export function socialShortRun(run: SocialShortRunInput): SocialShortRun | null {
  const groups = run.groups ?? []
  if (!run.stopReason || run.stopReason === 'target_met' || groups.length === 0) {
    return null
  }
  const target = groups.reduce((sum, group) => sum + group.target, 0)
  const accepted = groups.reduce((sum, group) => sum + group.accepted, 0)
  if (accepted >= target) {
    return null
  }
  const days = groups
    .map((group) => group.readDays)
    .filter((value): value is number => typeof value === 'number')
  return {
    kind: run.stopReason === 'exhausted' ? 'exhausted' : 'off_topic',
    readDays: days.length ? Math.max(...days) : null,
  }
}

/** The short-run line's i18n key and values; null when the run met its target. */
export type SocialShortRunKey =
  | 'socialListening.shortRun.offTopic'
  | 'socialListening.shortRun.exhaustedWindow'
  | 'socialListening.shortRun.exhaustedYear'
  | 'socialListening.shortRun.exhausted'
  | 'socialListening.shortRun.exhaustedSinceLast'

export function socialShortRunCopy(
  run: SocialShortRunInput,
): { key: SocialShortRunKey; values: { count?: number } } | null {
  const short = socialShortRun(run)
  if (!short) {
    return null
  }
  if (short.kind === 'off_topic') {
    return { key: 'socialListening.shortRun.offTopic', values: {} }
  }
  // An update reads only since the last one: "the last 0 days" would be wrong (plan Step 4c).
  const groups = run.groups ?? []
  if (groups.every((group) => group.kind === 'update')) {
    return { key: 'socialListening.shortRun.exhaustedSinceLast', values: {} }
  }
  if (short.readDays === null) {
    return { key: 'socialListening.shortRun.exhaustedWindow', values: {} }
  }
  return short.readDays >= 365
    ? { key: 'socialListening.shortRun.exhaustedYear', values: {} }
    : { key: 'socialListening.shortRun.exhausted', values: { count: Math.round(short.readDays) } }
}
